import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ModalBody, Form, FormGroup, Label, Input, Row, Col } from 'reactstrap'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import { ConfirmFooter } from '@/dialogs/common/confirmFooter'
import ErrorBoundary from '@/components/ErrorBoundary'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import { useLocalization } from '@/hooks/localization'
import { EnumDropDownList } from '@/components/enumDropDownList'
import { reportHeaderType } from '@/enums/reportHeaderType'
import { intMaxValue } from '@/utils/constants'
import { NumericInput } from '@/components/inputs'
import { reportDataType } from '@/enums/reportDataType'
import { NullableBooleanDropDownList } from '@/components/nullableBooleanDropDownList'
import CheckBox from '@/components/CheckBox'
import { aggregationType } from '@/enums/aggregationType'
import { definitionType } from '@/enums/definitionType'
import { DropDownList } from '@/components/dropDownList'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import { getStore } from '@/store'
import context from '@/context'

const ReportDesignerCellDetailDialog = React.memo(function ReportDesignerCellDetailDialog (props) {
  const { onAction, data, cellType, siblingCodes } = props
  const { localize } = useLocalization()
  const [state, setState] = useState(data)
  const [reportDimensionId, setReportDimensionId] = useState(null)
  const onChange = useCallback(e => {
    const propName = e.target.name
    setState(state.set(propName, e.target.value))
  }, [state])

  const reportDimensionItemId = state.get('reportDimensionItemId')
  useEffect(() => {
    const getReportDimensionId = () => {
      if (reportDimensionItemId) {
        context.api.qlikDimensionsService.getDimensionItem(reportDimensionItemId).then((e) => {
          setReportDimensionId(e.reportDimensionId)
        }).catch(() => {
          getStore().dispatch(dialogActions.openDialog({
            dialogType: dialogTypes.alert,
            title: 'Common.Error',
            content: ('Common.ServerInternalError')
          }))
        })
      }
    }
    getReportDimensionId()
  }, [reportDimensionItemId])
  const onDropDownChange = useCallback((e) => {
    const propName = e.props.name
    if (propName === 'definitionType' && e.value !== definitionType.enum.UserDef) {
      setState(state.set('userDefRowsNo', null))
    }

    setState(state.set(propName, e.value))
  }, [state])
  const onReportDimensionDropdownChange = useCallback((e) => {
    setReportDimensionId(e.value)
    setState(state.set('reportDimensionItemId', null))
  }, [state])
  const onNullableBoolChange = useCallback((e) => {
    const propName = e.props.name
    setState(state.set(propName, e.value))
  }, [state])
  const onCheckBoxChange = useCallback((checked, value) => {
    setState(state.set(value, checked || false))
  }, [state])
  const onSubmit = useCallback(() => {
    onAction(state)
  }, [onAction, state])
  const validateCode = useCallback(({ value }) => {
    if (siblingCodes.indexOf(value) >= 0) {
      return 'ReportDesigner.DuplicateCode'
    }
  }, [siblingCodes])

  const dimensionItemsDatasource = useCallback(request => {
    return context.api.qlikDimensionsService.getDimensionItemsList(request, reportDimensionId)
  }, [reportDimensionId])

  const isTemplate = cellType === 'template'
  const isColumn = cellType === 'column'
  const isRow = cellType === 'row'
  const isBody = cellType === 'body'
  const isSandwichHeader = state.get('reportHeaderType') === reportHeaderType.enum.SandwichHeader
  const siblings = useMemo(() => siblingCodes ? siblingCodes.toJS().map(code => ({ code: code, name: code })) : [], [siblingCodes])

  function renderTextInput (name, label, validation, inputProps) {
    return (
      <FormGroup>
        <Label>{localize(label)}</Label>
        <Validator name={name} value={state.get(name)} disabled={inputProps && inputProps.disabled} {...validation}>
          <Input value={state.get(name) || ''} name={name} onChange={onChange} {...inputProps} />
        </Validator>
      </FormGroup>
    )
  }
  function renderEnum (name, label, enumType, validation) {
    return (
      <FormGroup>
        <Label>{localize(label)}</Label>
        <Validator name={name} value={state.get(name)} {...validation}>
          <EnumDropDownList enumDefinition={enumType} value={state.get(name) || 0} name={name} onChange={onDropDownChange} />
        </Validator>
      </FormGroup>
    )
  }
  function renderInt (name, label, validation, inputProps) {
    return (
      <FormGroup>
        <Label>{localize(label)}</Label>
        <Validator name={name} value={state.get(name)} disabled={inputProps && inputProps.disabled} required min={1} max={intMaxValue} {...validation}>
          <NumericInput className='form-control' value={state.get(name)} onChange={val => setState(state.set(name, val))} {...inputProps} />
        </Validator>
      </FormGroup>
    )
  }
  function renderNullableBool (name, label) {
    return (
      <FormGroup>
        <Label>{localize(label)}</Label>
        <NullableBooleanDropDownList value={state.get(name)} name={name} onChange={onNullableBoolChange} />
      </FormGroup>
    )
  }
  function renderFormula (inputProps, validation, name = 'formula') {
    const disabled = cellType !== 'body' && state.get('aggregationType') !== aggregationType.enum.Calc
    return (
      <FormGroup>
        <Label>{localize('Reporting.Formula')}</Label>
        <Validator name={name} value={state.get(name)} disabled={disabled} {...validation}> {/* TODO Validate formula */}
          <Input value={state.get(name) || ''} name={name} onChange={onChange} disabled={disabled} type='textarea' rows='3' {...inputProps} />
        </Validator>
      </FormGroup>
    )
  }
  function renderCheckbox (name, label, disabled = false) {
    return (
      <FormGroup>
        <CheckBox title={label} value={name} checked={state.get(name)} onChange={onCheckBoxChange} disabled={disabled} />
      </FormGroup>
    )
  }
  function renderTextArea (name, label) {
    return (
      <FormGroup>
        <Label>{localize(label)}</Label>
        <Input
          name={name}
          type='textarea'
          rows='3'
          onChange={onChange}
          value={state.get(name) || ''} />
      </FormGroup>
    )
  }

  function renderSiblings (name) {
    return (
      <FormGroup>
        <Label>{localize('ReportDesigner.SiblingCode')}</Label>
        <DropDownList
          name={name}
          dataSource={siblings}
          value={state.get(name) || ''}
          onChange={onDropDownChange}
          textField='name'
          valueField='code'
          clientSide allowClear />
      </FormGroup>
    )
  }

  return (
    <ValidationBoundary>
      <BasicHeader title={getDialogTitle(cellType)} {...props} />
      <ModalBody>
        <ErrorBoundary>
          <Form>
            {isTemplate && (
              <React.Fragment>
                <Row>
                  <Col md={6}>
                    {renderTextInput('code', 'Common.Code', { required: true, regex: '[a-zA-Z0-9]+' })}
                    {renderTextInput('name', 'Common.Name', { required: true })}
                    {renderTextInput('latinName', 'Common.LatinName', { required: true })}
                    {renderTextInput('colAxisCode', 'ReportDesigner.ColAxisCode', { required: true })}
                    {renderTextInput('rowAxisCode', 'ReportDesigner.RowAxisCode', { required: true })}
                  </Col>
                  <Col>
                    {renderTextInput('title', 'Reporting.Title', { required: true })}
                    {renderTextInput('latinTitle', 'Reporting.LatinTitle', { required: true })}
                    {renderTextInput('rowTitle', 'Reporting.RowTitle', { required: true })}
                    {renderTextInput('latinRowTitle', 'Reporting.LatinRowTitle', { required: true })}
                    {renderInt('order', 'ReportDesigner.Order')}
                    {renderCheckbox('isEditableOutputReport', 'Reporting.IsEditableOutputReport')}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {renderTextArea('latinNotes', 'Reporting.LatinNotes')}
                  </Col>
                  <Col md={6}>
                    {renderTextArea('notes', 'Reporting.Notes')}
                  </Col>
                </Row>
              </React.Fragment>
            )}

            {isColumn && (
              <Row>
                <Col md={6}>
                  {renderTextInput('code', 'Common.Code', { required: true, regex: '[a-zA-Z0-9]+', onValidate: validateCode })}
                  {renderTextInput('name', 'Common.Name', { required: true })}
                  {renderTextInput('latinName', 'Common.LatinName', { required: true })}
                  {renderEnum('reportDataType', 'Reporting.ReportDataType', reportDataType)}
                  <FormGroup>
                    <Label>{localize('ReportDesigner.QlikDimension')}</Label>
                    <DropDownList textField={'name'} valueField={'id'} value={reportDimensionId} dataSource={context.api.qlikDimensionsService.getDimensionsList}
                      onChange={onReportDimensionDropdownChange} />
                  </FormGroup>
                  <FormGroup>
                    <Label>{localize('ReportDesigner.DimensionItem')}</Label>
                    <Validator name={'reportDimensionItemId'} value={state.get('reportDimensionItemId')} disabled={!reportDimensionId || reportDimensionId < 1}
                      required={reportDimensionId && reportDimensionId > 0}>
                      <DropDownList textField={'name'} valueField={'id'} value={state.get('reportDimensionItemId')} name={'reportDimensionItemId'} dataSource={dimensionItemsDatasource} onChange={onDropDownChange}
                        disabled={!reportDimensionId || reportDimensionId < 1} />
                    </Validator>
                  </FormGroup>
                </Col>
                <Col>
                  {renderNullableBool('dominant', 'Reporting.Dominant')}
                  {renderEnum('aggregationType', 'Reporting.AggregationType', aggregationType)}
                  {renderFormula(null, { required: true })}
                  {renderCheckbox('hasData', 'Reporting.HasData', state.get('children').size === 0)}
                  {renderCheckbox('aggregationAllowed', 'Reporting.AggregationAllowed')}
                </Col>
              </Row>
            )}

            {isRow && (
              <Row>
                <Col md={6}>
                  {renderTextInput('code', 'Common.Code', { required: true, regex: '[a-zA-Z0-9]+', onValidate: validateCode })}
                  {renderTextInput('name', 'Common.Name', { required: true })}
                  {renderTextInput('latinName', 'Common.LatinName', { required: true })}
                  {renderEnum('reportDataType', 'Reporting.ReportDataType', reportDataType)}
                  {renderEnum('definitionType', 'Reporting.DefinitionType', definitionType)}
                  {renderInt('userDefRowsNo', 'ReportDesigner.UserRowAmount', { required: false }, { disabled: state.get('definitionType') !== definitionType.enum.UserDef })}
                  {renderSiblings('siblingCode')}
                  <FormGroup>
                    <Label>{localize('ReportDesigner.QlikDimension')}</Label>
                    <DropDownList textField={'name'} valueField={'id'} value={reportDimensionId} dataSource={context.api.qlikDimensionsService.getDimensionsList}
                      onChange={onReportDimensionDropdownChange} />
                  </FormGroup>
                  <FormGroup>
                    <Label>{localize('ReportDesigner.DimensionItem')}</Label>
                    <Validator name={'reportDimensionItemId'} value={state.get('reportDimensionItemId')} disabled={!reportDimensionId || reportDimensionId < 1}
                      required={reportDimensionId && reportDimensionId > 0}>
                      <DropDownList textField={'name'} valueField={'id'} value={state.get('reportDimensionItemId')} name={'reportDimensionItemId'} dataSource={dimensionItemsDatasource} onChange={onDropDownChange}
                        disabled={!reportDimensionId || reportDimensionId < 1} />
                    </Validator>
                  </FormGroup>
                </Col>
                <Col>
                  {renderEnum('reportHeaderType', 'Reporting.ReportHeaderType', reportHeaderType)}
                  {renderTextInput('sandwichName', 'Reporting.SandwichName', { required: true }, { disabled: !isSandwichHeader })}
                  {renderTextInput('sandwichLatinName', 'Reporting.SandwichLatinName', { required: true }, { disabled: !isSandwichHeader })}
                  {renderNullableBool('dominant', 'Reporting.Dominant')}
                  {renderEnum('aggregationType', 'Reporting.AggregationType', aggregationType)}
                  {renderFormula(null, { required: true })}
                  {renderCheckbox('hasData', 'Reporting.HasData', state.get('children').size === 0)}
                  {renderCheckbox('aggregationAllowed', 'Reporting.AggregationAllowed')}
                </Col>
              </Row>
            )}

            {isBody && (
              <Row>
                <Col md={6}>
                  {renderTextInput('data', 'Reporting.CellData')}
                  {renderInt('colSpan', 'Reporting.ColSpan', { min: 1, max: 500 }, 1)}
                </Col>
                <Col>
                  {renderFormula()}
                  {renderCheckbox('isDisabled', 'Reporting.IsDisabled')}
                </Col>
              </Row>
            )}
          </Form>
        </ErrorBoundary>
      </ModalBody>
      <ValidationState render={({ isValid }) =>
        <ConfirmFooter primaryButtonText='Common.SaveAndClose' icon='save' {...props} onAction={onSubmit} disabled={!isValid} />
      } />
    </ValidationBoundary>
  )
})

function getDialogTitle (cellType) {
  switch (cellType) {
    case 'body': return 'ReportDesigner.BodyCellDetailDialogTitle'
    case 'column': return 'ReportDesigner.ColumnCellDetailDialogTitle'
    case 'row': return 'ReportDesigner.RowCellDetailDialogTitle'
  }
  return 'ReportDesigner.TemplateDetailDialogTitle'
}

export default {
  modalProps: {
    size: 'lg'
  },
  component: ReportDesignerCellDetailDialog
}



// WEBPACK FOOTER //
// ./src/components/reportDesigner/dialogs/cellDetailDialog.js